import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Typography, TabGroup } from '@dreamteam-monorepo/ui';
import { Dropdown, DropdownOption } from '../../../../../packages/ui/src/components/Dropdown';
import { SegmentedControl } from '../../../../../packages/ui/src/components/SegmentedControl';
import { twMerge } from 'tailwind-merge';
import { useFetchPlayerStats } from '@/hooks/useFetchPlayerStats';
import { useFetchFixtures } from '@/hooks/useFetchFixtures';
import { useTeamCreateTournamentCalendarId } from '@/hooks/useTeamCreateTournamentCalendarId';
import { usePeriodLabels } from '@/hooks/usePeriodLabels';
import { useUrlFilterState } from '@/hooks/useUrlFilterState';
import { getPeriodLabels, periodAxisFromFormat } from '@/lib/periodLabels';
import { fetchContestants } from '@/lib/network';
import { teamPagePrices } from '@/pages/Teams/components/teamPageConfig';
import {
  buildFixturePeriodOptions,
  excludePlayedFixturePeriodKeys,
  filterFixturePeriodKeys,
  getFixturePeriodKeys,
  getFixturePeriodLabel,
} from './fixturePeriods';
import { fixtureCompetitionTag } from './fixtureCompetitionTag';
import type { FixtureTeam, TeamFixture, FetchPlayerStatsParams } from '@/lib/network';
import { ContentAdSlot } from '@/ads';
import { trackFantasyEvent } from '@/analytics';
import { PlayerRow } from '@/components/StatsCenter/PlayerRow';
import {
  CATEGORY_COLUMNS,
  PERIOD_POINTS_COLUMN_KEY,
  PPM_COLUMNS,
  STATS_COLUMNS,
} from '@/components/StatsCenter/constants';
import { Position } from '@/components/StatsCenter/types';
import { normalizePosition } from '@/components/StatsCenter/helpers';
import { TopTransfersPanel } from './TopTransfersPanel';

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = 'player-stats' | 'fixtures' | 'top-transfers';
type SortDir = 'asc' | 'desc' | null;

interface SortState {
  column: string | null;
  dir: SortDir;
}

interface ActiveFilter {
  id: string;
  label: string;
  groupId: string;
}

interface PlayerStatsFilters {
  positions: Position[];
  teams: string[];
  price: string[];
  category: string[];
  ppmMetrics: string[];
}

interface FixturesFilters {
  teams: string[];
  matchdays: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const POSITIONS: Position[] = ['ALL', 'GK', 'DEF', 'MID', 'STR'];

type PriceRange = DropdownOption & { test: (price: number) => boolean };

const PRICE_RANGE_TESTS: Record<string, PriceRange['test']> = {
  '0-2': price => price >= 0 && price <= 2,
  '2-4': price => price > 2 && price <= 4,
  '4-6': price => price > 4 && price <= 6,
  '6-99': price => price > 6,
};

const PRICE_RANGES: PriceRange[] = teamPagePrices
  .filter(option => option.value !== 'afford')
  .map(option => ({
    ...option,
    test: PRICE_RANGE_TESTS[String(option.value)],
  }))
  .filter((option): option is PriceRange => option.test !== undefined);

const priceOptions: DropdownOption[] = [
  { id: 'all', label: 'All Prices', value: 'all' },
  ...PRICE_RANGES,
];

const categoryOptions: DropdownOption[] = [
  { id: 'goals', label: 'Goals', value: 'goals' },
  { id: 'assists', label: 'Assists', value: 'assists' },
  { id: 'cs', label: 'Clean Sheets', value: 'cs' },
  { id: 'shotsOnTarget', label: 'Shots on Target', value: 'shotsOnTarget' },
  { id: 'chancesCreated', label: 'Big Chances Created', value: 'chancesCreated' },
  { id: 'tackles', label: 'Tackles', value: 'tackles' },
  { id: 'saves', label: 'Saves', value: 'saves' },
  { id: 'goalsConceded', label: 'Goals Conceded', value: 'goalsConceded' },
  { id: 'ownGoals', label: 'Own Goals', value: 'ownGoals' },
  { id: 'yellowCards', label: 'Yellow Cards', value: 'yellowCards' },
  { id: 'redCards', label: 'Red Cards', value: 'redCards' },
  { id: 'penaltyMisses', label: 'Penalty Misses', value: 'penaltyMisses' },
  { id: 'penaltySaves', label: 'Penalty Saves', value: 'penaltySaves' },
];

const ppmOptions: DropdownOption[] = [
  { id: 'dribble', label: 'Successful Dribble', value: 'dribble' },
  { id: 'cross', label: 'Accurate Cross', value: 'cross' },
  { id: 'offside', label: 'Offside', value: 'offside' },
  { id: 'passCompletion', label: 'Pass Completion %', value: 'passCompletion' },
  { id: 'interception', label: 'Interception', value: 'interception' },
  { id: 'block', label: 'Block', value: 'block' },
  { id: 'goalOutside', label: 'Goal Outside Area', value: 'goalOutside' },
  { id: 'foulWon', label: 'Foul Won', value: 'foulWon' },
  { id: 'foulConceded', label: 'Foul Conceded', value: 'foulConceded' },
  { id: 'errorToGoal', label: 'Error Leading To Goal', value: 'errorToGoal' },
  { id: 'claim', label: 'Successful Claim', value: 'claim' },
  { id: 'punch', label: 'Successful Punch', value: 'punch' },
  { id: 'keeperSweep', label: 'Keeper Sweep', value: 'keeperSweep' },
];

// Column keys the backend accepts as a sort value (PlayerStatsSortByEnum in the API's
// player.schema.ts). A column whose key is NOT here renders as a plain, non-sortable
// header, and posting an unlisted sortBy would get a 400 from the API.
const SORTABLE_COLUMN_KEYS = new Set<string>([
  'price',
  'percentSelected',
  'gwPts',
  'avgPts',
  'last3Avg',
  'bonusPoints',
  'ppmPoints',
  'pts',
  'dribble',
  'cross',
  'offside',
  'passCompletion',
  'interception',
  'block',
  'goalOutside',
  'foulWon',
  'foulConceded',
  'errorToGoal',
  'claim',
  'punch',
  'keeperSweep',
  'goals',
  'assists',
  'cs',
  'shotsOnTarget',
  'chancesCreated',
  'tackles',
  'saves',
  'goalsConceded',
  'ownGoals',
  'yellowCards',
  'redCards',
  'penaltyMisses',
  'penaltySaves',
]);

// ─── Sub-components ───────────────────────────────────────────────────────────

function SearchInput({ value, onChange }: { value: string; onChange: (val: string) => void }) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Search players…"
        className="h-10 pl-10 pr-3 bg-[#f5f5ff] rounded-[--corner-xs] text-sm text-[#030033] placeholder:text-[#888] focus:outline-none focus:ring-2 focus:ring-[#0072ee] w-full min-w-[180px]"
      />
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2"
        width="16"
        height="16"
        viewBox="0 0 20 20"
        fill="none"
      >
        <circle cx="8.5" cy="8.5" r="5.75" stroke="#030033" strokeWidth="1.5" />
        <path d="M13.5 13.5L17 17" stroke="#030033" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function ActiveFilterChips({
  filters,
  onRemove,
  onClear,
}: {
  filters: ActiveFilter[];
  onRemove: (id: string) => void;
  onClear: () => void;
}) {
  if (filters.length === 0) return null;
  return (
    <div className="flex flex-col px-4 py-3 bg-white">
      <span className="text-sm font-medium text-[#030033] mb-2">Filters:</span>
      <div className="flex flex-wrap items-center gap-2">
        {filters.map(f => (
          <span
            key={f.id}
            className="flex items-center gap-1 h-8 px-3 rounded-full border border-[#0072ee] text-[#0072ee] text-xs font-medium"
          >
            {f.label}
            <button
              onClick={() => onRemove(f.id)}
              className="flex items-center justify-center ml-0.5 hover:opacity-70"
              aria-label={`Remove ${f.label} filter`}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M9 3L3 9M3 3l6 6"
                  stroke="#0072ee"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </span>
        ))}
        <button
          onClick={onClear}
          className="h-8 px-3 rounded-full bg-[#0072ee] text-white text-xs font-bold hover:bg-[#005fcc] transition-colors"
        >
          Clear Filters
        </button>
      </div>
    </div>
  );
}

function SortChevron({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <span className="inline-flex flex-col gap-[1px] ml-1">
      <svg width="8" height="10" viewBox="0 0 8 10" fill="none">
        <path d="M4 1L7 4H1L4 1Z" fill={active && dir === 'asc' ? '#030033' : '#aaa'} />
        <path d="M4 9L1 6H7L4 9Z" fill={active && dir === 'desc' ? '#030033' : '#aaa'} />
      </svg>
    </span>
  );
}

function StatsColumnHeaders({
  sort,
  onSort,
  activeCategoryKeys,
  activePpmKeys,
}: {
  sort: SortState;
  onSort: (col: string) => void;
  activeCategoryKeys: string[];
  activePpmKeys: string[];
}) {
  const { points: periodPointsLabel } = usePeriodLabels();
  const activeCategoryColumns = CATEGORY_COLUMNS.filter(col =>
    activeCategoryKeys.includes(col.key)
  );
  const activePpmColumns = PPM_COLUMNS.filter(col => activePpmKeys.includes(col.key));
  const columnLabel = (col: { key: string; label: string }) =>
    col.key === PERIOD_POINTS_COLUMN_KEY ? periodPointsLabel : col.label;
  return (
    <div className="flex items-center bg-white border-t border-[#eae9fd] px-4 py-3 w-full">
      <div className="flex-shrink-0 w-[240px]">
        <Typography variant="caption010" className="text-[#030033] font-bold uppercase">
          Player
        </Typography>
      </div>
      {[...STATS_COLUMNS, ...activeCategoryColumns, ...activePpmColumns].map(col => {
        // Detail stats the API can't sort by render as a plain, non-interactive label.
        if (!SORTABLE_COLUMN_KEYS.has(col.key)) {
          return (
            <div key={col.key} className="flex-1 flex items-center justify-center">
              <Typography variant="caption010" className="font-bold uppercase text-[#888]">
                {columnLabel(col)}
              </Typography>
            </div>
          );
        }
        return (
          <button
            key={col.key}
            onClick={() => onSort(col.key)}
            className="flex-1 flex items-center justify-center hover:opacity-70 transition-opacity"
          >
            <Typography
              variant="caption010"
              className={twMerge(
                'font-bold uppercase',
                sort.column === col.key ? 'text-[#030033]' : 'text-[#888]'
              )}
            >
              {columnLabel(col)}
            </Typography>
            <SortChevron active={sort.column === col.key} dir={sort.dir} />
          </button>
        );
      })}
    </div>
  );
}

// ─── Filter bars ──────────────────────────────────────────────────────────────

function useMultiSelect<T extends object, K extends keyof T>(
  key: K,
  state: T & Record<K, string[]>,
  setState: React.Dispatch<React.SetStateAction<T>>
) {
  return useCallback(
    (option: DropdownOption, checked: boolean) => {
      setState(prev => {
        const cur = prev[key] as string[];
        if (option.value === 'all' || cur.includes('all')) {
          return { ...prev, [key]: checked && option.value === 'all' ? ['all'] : [option.value] };
        }
        const next = checked ? [...cur, option.value] : cur.filter(v => v !== option.value);
        return { ...prev, [key]: next.length === 0 ? ['all'] : next };
      });
    },
    [key, setState]
  );
}

function trackStatsFilterChange(
  tabName: 'Player Stats' | 'Fixtures',
  filterName: string,
  filterValue: string,
  selected: boolean
) {
  trackFantasyEvent('statsCentreFilter', {
    filter_name: `${tabName} - ${filterName}`,
    filter_value: `${selected ? 'Select' : 'Remove'} ${filterValue}`,
  });
}

function PlayerStatsFilterBar({
  filters,
  onChange,
  teamOptions,
  searchText,
  onSearchChange,
}: {
  filters: PlayerStatsFilters;
  onChange: React.Dispatch<React.SetStateAction<PlayerStatsFilters>>;
  teamOptions: DropdownOption[];
  searchText: string;
  onSearchChange: (val: string) => void;
}) {
  const updateTeams = useMultiSelect('teams', filters, onChange);
  const updatePrice = useMultiSelect('price', filters, onChange);
  const updateCategory = useMultiSelect('category', filters, onChange);
  const updatePpm = useMultiSelect('ppmMetrics', filters, onChange);
  const handleTeams = (option: DropdownOption, checked: boolean) => {
    updateTeams(option, checked);
    trackStatsFilterChange('Player Stats', 'Teams', option.label, checked);
  };
  const handlePrice = (option: DropdownOption, checked: boolean) => {
    updatePrice(option, checked);
    trackStatsFilterChange('Player Stats', 'Price', option.label, checked);
  };
  const handleCategory = (option: DropdownOption, checked: boolean) => {
    updateCategory(option, checked);
    trackStatsFilterChange('Player Stats', 'Category', option.label, checked);
  };
  const handlePpm = (option: DropdownOption, checked: boolean) => {
    updatePpm(option, checked);
    trackStatsFilterChange('Player Stats', 'PPM Metrics', option.label, checked);
  };

  return (
    <div className="flex flex-wrap items-end gap-2 py-3 bg-white  ">
      <div className="flex flex-col gap-2">
        <span className="text-xs text-[--inputs-label-default] ">Search</span>
        <SearchInput value={searchText} onChange={onSearchChange} />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs text-[--inputs-label-default]">Position</span>
        <SegmentedControl
          items={POSITIONS.map(pos => ({ id: pos, label: pos }))}
          selectedIds={filters.positions}
          multiple
          onChange={ids => {
            const next = ids as Position[];
            const last = next[next.length - 1];
            const changedPosition =
              next.find(position => !filters.positions.includes(position)) ??
              filters.positions.find(position => !next.includes(position));
            if (last === 'ALL') {
              onChange(p => ({ ...p, positions: ['ALL'] }));
            } else {
              onChange(p => ({ ...p, positions: next.filter(pos => pos !== 'ALL') }));
            }
            if (changedPosition) {
              trackStatsFilterChange(
                'Player Stats',
                'Position',
                changedPosition,
                next.includes(changedPosition)
              );
            }
          }}
        />
      </div>
      <div className="flex gap-2 ml-auto flex-wrap items-end">
        <div className="flex flex-col gap-2">
          <span className="text-xs text-[color:var(--inputs-label-default,#222526)]">Teams</span>
          <Dropdown
            options={teamOptions}
            values={filters.teams}
            multiple
            onValueChange={handleTeams}
            emptyValue="Teams"
            className="w-[139px]"
            onWhite
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs text-[color:var(--inputs-label-default,#222526)]">Price</span>
          <Dropdown
            options={priceOptions}
            values={filters.price}
            multiple
            onValueChange={handlePrice}
            emptyValue="Price"
            className="w-[139px]"
            onWhite
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs text-[color:var(--inputs-label-default,#222526)]">Category</span>
          <Dropdown
            options={categoryOptions}
            values={filters.category}
            multiple
            onValueChange={handleCategory}
            emptyValue="Category"
            className="w-[139px]"
            onWhite
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs text-[color:var(--inputs-label-default,#222526)]">
            PPM Metrics
          </span>
          <Dropdown
            options={ppmOptions}
            values={filters.ppmMetrics}
            multiple
            onValueChange={handlePpm}
            emptyValue="PPM Metrics"
            className="w-[139px]"
            onWhite
          />
        </div>
      </div>
    </div>
  );
}

function FixturesFilterBar({
  filters,
  onChange,
  teamOptions,
  periodOptions,
  periodEmptyValue,
}: {
  filters: FixturesFilters;
  onChange: React.Dispatch<React.SetStateAction<FixturesFilters>>;
  teamOptions: DropdownOption[];
  periodOptions: DropdownOption[];
  periodEmptyValue: string;
}) {
  const updateTeams = useMultiSelect('teams', filters, onChange);
  const updateMatchdays = useMultiSelect('matchdays', filters, onChange);
  const handleTeams = (option: DropdownOption, checked: boolean) => {
    updateTeams(option, checked);
    trackStatsFilterChange('Fixtures', 'Teams', option.label, checked);
  };
  const handleMatchdays = (option: DropdownOption, checked: boolean) => {
    updateMatchdays(option, checked);
    trackStatsFilterChange('Fixtures', periodEmptyValue, option.label, checked);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-white border-t border-[#eae9fd]">
      <Dropdown
        options={teamOptions}
        values={filters.teams}
        multiple
        onValueChange={handleTeams}
        emptyValue="Teams"
        className="w-[139px]"
        onWhite
      />
      <Dropdown
        options={periodOptions}
        values={filters.matchdays}
        multiple
        onValueChange={handleMatchdays}
        emptyValue={periodEmptyValue}
        className="w-[139px]"
        onWhite
      />
    </div>
  );
}

// ─── Active filter chip builder ───────────────────────────────────────────────

function buildActiveFilters(
  tab: Tab,
  playerFilters: PlayerStatsFilters,
  fixtureFilters: FixturesFilters,
  teamOptions: DropdownOption[],
  fixturePeriodOptions: DropdownOption[]
): ActiveFilter[] {
  const chips: ActiveFilter[] = [];

  if (tab === 'player-stats') {
    playerFilters.positions
      .filter(pos => pos !== 'ALL')
      .forEach(pos => chips.push({ id: `pos:${pos}`, label: pos, groupId: 'position' }));
    playerFilters.teams
      .filter(v => v !== 'all')
      .forEach(v => {
        const opt = teamOptions.find(o => o.value === v);
        if (opt) chips.push({ id: `team:${v}`, label: opt.label, groupId: 'teams' });
      });
    playerFilters.price
      .filter(v => v !== 'all')
      .forEach(v => {
        const opt = priceOptions.find(o => o.value === v);
        if (opt) chips.push({ id: `price:${v}`, label: opt.label, groupId: 'price' });
      });
    playerFilters.category
      .filter(v => v !== 'all')
      .forEach(v => {
        const opt = categoryOptions.find(o => o.value === v);
        if (opt) chips.push({ id: `cat:${v}`, label: opt.label, groupId: 'category' });
      });
    playerFilters.ppmMetrics
      .filter(v => v !== 'all')
      .forEach(v => {
        const opt = ppmOptions.find(o => o.value === v);
        if (opt) chips.push({ id: `ppm:${v}`, label: opt.label, groupId: 'ppmMetrics' });
      });
  } else {
    fixtureFilters.teams
      .filter(v => v !== 'all')
      .forEach(v => {
        const opt = teamOptions.find(o => o.value === v);
        if (opt) chips.push({ id: `team:${v}`, label: opt.label, groupId: 'teams' });
      });
    fixtureFilters.matchdays
      .filter(v => v !== 'all')
      .forEach(v => {
        const opt = fixturePeriodOptions.find(o => o.value === v);
        if (opt) chips.push({ id: `md:${v}`, label: opt.label, groupId: 'matchdays' });
      });
  }

  return chips;
}

const PLACEHOLDER_FIXTURE_TEAM_LABELS = new Set(['TBC', 'TBD']);

function getFixtureVenueSuffix(fixture: TeamFixture): string {
  if (fixture.homeAway !== 'H' && fixture.homeAway !== 'A') {
    return '';
  }

  return ` (${fixture.homeAway})`;
}

function getFixtureCompetitionTag(fixture: TeamFixture): string | null {
  return fixtureCompetitionTag(fixture);
}

function getFixtureOpponentLabel(fixture: TeamFixture): string {
  const opponent =
    fixture.opponentFlagKey ||
    fixture.opponentShortName?.trim() ||
    fixture.opponentName?.trim() ||
    'TBC';

  return `${opponent}${getFixtureVenueSuffix(fixture)}`;
}

function getFixtureTeamLabel(team: FixtureTeam): string | null {
  const label = team.shortName?.trim() || team.name?.trim();

  if (!label || PLACEHOLDER_FIXTURE_TEAM_LABELS.has(label.toUpperCase())) {
    return null;
  }

  return label;
}

function getFixtureTeamFilterLabel(team: FixtureTeam): string | null {
  const label = team.name?.trim() || team.shortName?.trim();

  if (!label || PLACEHOLDER_FIXTURE_TEAM_LABELS.has(label.toUpperCase())) {
    return null;
  }

  return label;
}

function FixtureCellContent({
  fixtures,
  align = 'center',
}: {
  fixtures: TeamFixture[];
  align?: 'center' | 'start';
}) {
  if (fixtures.length === 0) {
    return <span className="text-xs font-medium text-[#aaa]">-</span>;
  }

  return (
    <div
      className={twMerge(
        'flex w-full flex-col gap-1',
        align === 'center' ? 'items-center' : 'items-start'
      )}
    >
      {fixtures.map((fixture, index) => (
        <span
          key={`${fixture.opponentId}-${index}`}
          className={twMerge(
            'inline-flex max-w-full items-center justify-center rounded-[--corner-full] border border-[#d8d7ee] bg-white px-2.5 py-1 shadow-[0px_1px_0px_0px_rgba(34,37,38,0.04)]',
            align === 'center' ? 'w-full' : ''
          )}
        >
          <Typography
            variant="paragraph010"
            className="w-full whitespace-nowrap text-center font-bold text-[#222526]"
            title={`${fixture.opponentShortName}${getFixtureVenueSuffix(fixture)}${
              fixture.adopted && fixture.competitionName ? ` · ${fixture.competitionName}` : ''
            }`}
          >
            {getFixtureOpponentLabel(fixture)}
            {getFixtureCompetitionTag(fixture) ? (
              // Own line so the opponent label never wraps mid-pill in a narrow column.
              <span
                data-testid="fixture-competition-tag"
                className="block text-[9px] font-semibold uppercase leading-none tracking-wide text-[#6b6b8f]"
              >
                {getFixtureCompetitionTag(fixture)}
              </span>
            ) : null}
          </Typography>
        </span>
      ))}
    </div>
  );
}

// ─── Fixture Grid ─────────────────────────────────────────────────────────────

function FixtureGrid({
  teams,
  periodKeys,
  selectedTeams,
}: {
  teams: FixtureTeam[];
  periodKeys: string[];
  selectedTeams: string[];
}) {
  const periodLabels = usePeriodLabels();
  // Rows are EPL sides only (`primary`); their cup / European games still show
  // in the cells. Opponents that only exist through adopted fixtures get no row.
  const displayableTeams = teams.filter(
    team => team.primary !== false && getFixtureTeamLabel(team) !== null
  );
  const visibleTeams = selectedTeams.includes('all')
    ? displayableTeams
    : displayableTeams.filter(t => selectedTeams.includes(t.id));

  if (visibleTeams.length === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <Typography variant="paragraph020" className="text-[--color-neutral-600]">
          No teams match the selected filters.
        </Typography>
      </div>
    );
  }

  if (periodKeys.length === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <Typography variant="paragraph020" className="text-[--color-neutral-600]">
          No fixtures available.
        </Typography>
      </div>
    );
  }

  const fixtureGridTemplateColumns = `minmax(136px, 1.15fr) repeat(${periodKeys.length}, minmax(104px, 1fr))`;
  const lastMobileRowStartIndex =
    periodKeys.length % 2 === 0 ? periodKeys.length - 2 : periodKeys.length - 1;

  return (
    <div className="w-full">
      <div className="overflow-auto rounded-[--corner-md] border border-[#eae9fd] bg-white shadow-[0px_4px_24px_0px_rgba(34,37,38,0.08)] md:block">
        <div
          className="grid bg-[#f5f5ff]"
          style={{ gridTemplateColumns: fixtureGridTemplateColumns }}
        >
          <div className="flex min-h-11 items-center border-b border-[#d8d7ee] px-4 py-3">
            <Typography variant="caption010" className="font-bold uppercase text-[#222526]">
              Team
            </Typography>
          </div>
          {periodKeys.map(periodKey => (
            <div
              key={periodKey}
              className="flex min-h-11 items-center justify-center border-l border-b border-[#eae9fd] bg-[#f5f5ff] px-2 py-3"
            >
              <Typography variant="caption010" className="font-bold uppercase text-[#222526]">
                {getFixturePeriodLabel(periodKey, periodLabels)}
              </Typography>
            </div>
          ))}
        </div>

        {visibleTeams.map(team => {
          const teamLabel = getFixtureTeamLabel(team) ?? team.shortName ?? team.name;

          return (
            <div
              key={team.id}
              className="grid min-h-[64px] last:border-b-0 hover:bg-[#fbfbff]"
              style={{ gridTemplateColumns: fixtureGridTemplateColumns }}
            >
              <div className="flex items-center border-b border-[#eae9fd] bg-[#f5f5ff] px-4 py-3">
                <Typography variant="paragraph010" className="truncate !font-bold text-[#222526]">
                  {teamLabel}
                </Typography>
              </div>

              {periodKeys.map(periodKey => (
                <div
                  key={periodKey}
                  className="flex min-w-0 items-center justify-center border-l border-b border-[#eae9fd] px-2 py-2"
                >
                  <FixtureCellContent fixtures={team.fixtures[periodKey] ?? []} />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// Module-level so the identity is stable across renders — useUrlFilterState
// memoises on it. Keys are prefixed because `teams` exists in both filter sets.
const FILTER_DEFAULTS = {
  tab: 'player-stats',
  q: '',
  p_pos: ['ALL'],
  p_teams: ['all'],
  p_price: ['all'],
  p_cat: ['all'],
  p_ppm: ['all'],
  f_teams: ['all'],
  f_period: ['all'],
};

type StatsUrlState = typeof FILTER_DEFAULTS;

const toPlayerFilters = (state: StatsUrlState): PlayerStatsFilters => ({
  positions: state.p_pos as Position[],
  teams: state.p_teams,
  price: state.p_price,
  category: state.p_cat,
  ppmMetrics: state.p_ppm,
});

const toFixtureFilters = (state: StatsUrlState): FixturesFilters => ({
  teams: state.f_teams,
  matchdays: state.f_period,
});

export default function StatsCentre() {
  const { id: tournamentCalendarId, format: tournamentFormat } =
    useTeamCreateTournamentCalendarId();
  const periodAxis = periodAxisFromFormat(tournamentFormat);
  const periodLabels = getPeriodLabels(periodAxis);
  // Tab, search and both filter sets live in the query string so they survive
  // opening a player profile and coming back, plus a refresh or a shared link.
  const { values: urlState, setValues: setUrlState } = useUrlFilterState(FILTER_DEFAULTS);
  const [sort, setSort] = useState<SortState>({ column: 'pts', dir: 'desc' });
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  const activeTab = urlState.tab as Tab;
  const setActiveTab = useCallback(
    (tab: Tab) => setUrlState(prev => ({ ...prev, tab })),
    [setUrlState]
  );

  const searchText = urlState.q;
  const setSearchText = useCallback(
    (q: string) => setUrlState(prev => ({ ...prev, q })),
    [setUrlState]
  );

  // Debounce search input: API call fires 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // The flat URL keys are projected back into the nested shapes the filter bars
  // and the chip helpers already expect, so those stay untouched.
  const playerFilters = useMemo<PlayerStatsFilters>(() => toPlayerFilters(urlState), [urlState]);

  const setPlayerFilters = useCallback<React.Dispatch<React.SetStateAction<PlayerStatsFilters>>>(
    next =>
      setUrlState(prev => {
        const resolved = typeof next === 'function' ? next(toPlayerFilters(prev)) : next;
        return {
          ...prev,
          p_pos: resolved.positions,
          p_teams: resolved.teams,
          p_price: resolved.price,
          p_cat: resolved.category,
          p_ppm: resolved.ppmMetrics,
        };
      }),
    [setUrlState]
  );

  const fixtureFilters = useMemo<FixturesFilters>(() => toFixtureFilters(urlState), [urlState]);

  const setFixtureFilters = useCallback<React.Dispatch<React.SetStateAction<FixturesFilters>>>(
    next =>
      setUrlState(prev => {
        const resolved = typeof next === 'function' ? next(toFixtureFilters(prev)) : next;
        return { ...prev, f_teams: resolved.teams, f_period: resolved.matchdays };
      }),
    [setUrlState]
  );

  // Build API filters from UI state
  const apiFilters = useMemo<FetchPlayerStatsParams>(() => {
    const params: FetchPlayerStatsParams = {};

    params.type = periodAxis;

    if (tournamentCalendarId) {
      params.tournamentCalendarId = tournamentCalendarId;
    }

    if (debouncedSearchText.trim()) {
      params.search = debouncedSearchText.trim();
    }

    // Positions: send all selected as comma-separated string
    const selectedPositions = playerFilters.positions.filter(p => p !== 'ALL');
    if (selectedPositions.length > 0) {
      params.positions = selectedPositions.join(',');
    }

    // Teams: send all selected IDs as comma-separated string
    const selectedTeams = playerFilters.teams.filter(t => t !== 'all');
    if (selectedTeams.length > 0) {
      params.contestantIds = selectedTeams.join(',');
    }

    // Price ranges: send all selected as comma-separated string
    const selectedPrices = playerFilters.price.filter(p => p !== 'all');
    if (selectedPrices.length > 0) {
      params.priceRanges = selectedPrices.join(',');
    }

    if (sort.column && sort.dir) {
      params.sortBy = sort.column;
      params.sortDir = sort.dir;
    }

    return params;
  }, [
    tournamentCalendarId,
    periodAxis,
    debouncedSearchText,
    playerFilters.positions,
    playerFilters.teams,
    playerFilters.price,
    sort.column,
    sort.dir,
  ]);

  const {
    data: playerPages,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchPlayerStats(apiFilters, { enabled: activeTab === 'player-stats' });

  const {
    data: fixturesData,
    isLoading: fixturesLoading,
    isError: fixturesError,
  } = useFetchFixtures(
    // All competitions: cup / European fixtures show in the EPL teams' cells
    // (ENG-1135). Non-EPL opponents are kept out of the rows via `primary`.
    { type: periodAxis },
    { enabled: activeTab === 'fixtures' }
  );

  // Flatten accumulated pages
  const allPlayers = useMemo(
    () => playerPages?.pages.flatMap(page => page.data.items) ?? [],
    [playerPages]
  );

  // Build teams dropdown from live data (fixtures tab uses fixture teams when available)
  const fixtureTeamOptions = useMemo<DropdownOption[]>(() => {
    const opts: DropdownOption[] = [{ id: 'all', label: 'All Teams', value: 'all' }];
    for (const t of fixturesData?.teams ?? []) {
      if (t.primary === false) continue;
      const label = getFixtureTeamFilterLabel(t);

      if (!label) {
        continue;
      }

      opts.push({ id: t.id, label, value: t.id });
    }
    return opts;
  }, [fixturesData]);

  // Fetch full team list from contestants API (same as MatchCentre)
  const contestantsQuery = useQuery({
    queryKey: ['contestants', 'list', tournamentCalendarId],
    queryFn: () => fetchContestants(tournamentCalendarId ?? undefined),
    enabled: activeTab === 'player-stats' && !!tournamentCalendarId,
  });

  // Build teams dropdown from contestants API
  const teamOptions = useMemo<DropdownOption[]>(() => {
    const base = [{ id: 'all', label: 'All Teams', value: 'all' }];
    const teams = contestantsQuery.data?.data ?? [];
    return [
      ...base,
      ...teams.map(t => ({
        id: t.id,
        label: t.name ?? t.shortName ?? 'Unknown Team',
        value: t.id,
      })),
    ];
  }, [contestantsQuery.data]);

  // Apply backup client-side filters only; the API owns sorting before pagination.
  const visiblePlayers = useMemo(() => {
    let result = allPlayers;

    // Client-side position filter (when multiple positions selected or API didn't handle it)
    if (!playerFilters.positions.includes('ALL')) {
      result = result.filter(p => playerFilters.positions.includes(normalizePosition(p.position)));
    }

    // Client-side team filter (when multiple teams selected or API didn't handle it)
    if (!playerFilters.teams.includes('all')) {
      result = result.filter(p => p.contestantId && playerFilters.teams.includes(p.contestantId));
    }

    // Client-side price filter (backup when API couldn't handle multiple ranges precisely)
    if (!playerFilters.price.includes('all')) {
      const ranges = PRICE_RANGES.filter(r => playerFilters.price.includes(r.value));
      result = result.filter(p => ranges.some(r => r.test(p.price)));
    }

    return result;
  }, [allPlayers, playerFilters]);

  const activeFilterTeamOptions = activeTab === 'fixtures' ? fixtureTeamOptions : teamOptions;
  const fixturePeriodKeys = useMemo(() => getFixturePeriodKeys(fixturesData), [fixturesData]);
  const fixturePeriodOptions = useMemo(
    () => buildFixturePeriodOptions(fixturePeriodKeys, periodLabels),
    [fixturePeriodKeys, periodLabels]
  );

  const handleSort = useCallback((column: string) => {
    setSort(prev => {
      if (prev.column !== column) return { column, dir: 'desc' };
      if (prev.dir === 'desc') return { column, dir: 'asc' };
      return { column: null, dir: null };
    });
  }, []);

  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      const removedFilter = buildActiveFilters(
        activeTab,
        playerFilters,
        fixtureFilters,
        activeFilterTeamOptions,
        fixturePeriodOptions
      ).find(filter => filter.id === filterId);

      if (activeTab === 'player-stats') {
        if (filterId.startsWith('pos:')) {
          const val = filterId.slice(4) as Position;
          setPlayerFilters(p => {
            const next = p.positions.filter(pos => pos !== val);
            return { ...p, positions: next.length === 0 ? ['ALL'] : next };
          });
        } else if (filterId.startsWith('team:')) {
          const val = filterId.slice(5);
          setPlayerFilters(p => {
            const next = p.teams.filter(v => v !== val);
            return { ...p, teams: next.length === 0 ? ['all'] : next };
          });
        } else if (filterId.startsWith('price:')) {
          const val = filterId.slice(6);
          setPlayerFilters(p => {
            const next = p.price.filter(v => v !== val);
            return { ...p, price: next.length === 0 ? ['all'] : next };
          });
        } else if (filterId.startsWith('cat:')) {
          const val = filterId.slice(4);
          setPlayerFilters(p => {
            const next = p.category.filter(v => v !== val);
            return { ...p, category: next.length === 0 ? ['all'] : next };
          });
        } else if (filterId.startsWith('ppm:')) {
          const val = filterId.slice(4);
          setPlayerFilters(p => {
            const next = p.ppmMetrics.filter(v => v !== val);
            return { ...p, ppmMetrics: next.length === 0 ? ['all'] : next };
          });
        }
      } else {
        if (filterId.startsWith('team:')) {
          const val = filterId.slice(5);
          setFixtureFilters(p => {
            const next = p.teams.filter(v => v !== val);
            return { ...p, teams: next.length === 0 ? ['all'] : next };
          });
        } else if (filterId.startsWith('md:')) {
          const val = filterId.slice(3);
          setFixtureFilters(p => {
            const next = p.matchdays.filter(v => v !== val);
            return { ...p, matchdays: next.length === 0 ? ['all'] : next };
          });
        }
      }

      if (removedFilter) {
        const removedFilterName =
          removedFilter.groupId === 'ppmMetrics'
            ? 'PPM Metrics'
            : removedFilter.groupId === 'matchdays'
              ? periodLabels.long
              : `${removedFilter.groupId.charAt(0).toUpperCase()}${removedFilter.groupId.slice(1)}`;
        trackStatsFilterChange(
          activeTab === 'player-stats' ? 'Player Stats' : 'Fixtures',
          removedFilterName,
          removedFilter.label,
          false
        );
      }
    },
    [
      activeFilterTeamOptions,
      activeTab,
      fixtureFilters,
      fixturePeriodOptions,
      periodLabels.long,
      playerFilters,
    ]
  );

  const handleClearFilters = useCallback(() => {
    if (activeTab === 'player-stats') {
      setPlayerFilters({
        positions: ['ALL'],
        teams: ['all'],
        price: ['all'],
        category: ['all'],
        ppmMetrics: ['all'],
      });
      setSearchText('');
    } else {
      setFixtureFilters({ teams: ['all'], matchdays: ['all'] });
    }
    trackFantasyEvent('statsCentreFilter', {
      filter_name: activeTab === 'player-stats' ? 'Player Stats' : 'Fixtures',
      filter_value: 'Clear all',
    });
  }, [activeTab]);

  const activeFilters = buildActiveFilters(
    activeTab,
    playerFilters,
    fixtureFilters,
    activeFilterTeamOptions,
    fixturePeriodOptions
  );
  // With no period picked the grid hides periods that have been played out; an
  // explicit pick from the dropdown wins, so played periods stay reachable.
  const visibleFixturePeriodKeys = useMemo(() => {
    const selectedPeriodKeys = filterFixturePeriodKeys(fixturePeriodKeys, fixtureFilters.matchdays);

    return fixtureFilters.matchdays.includes('all')
      ? excludePlayedFixturePeriodKeys(selectedPeriodKeys, fixturesData)
      : selectedPeriodKeys;
  }, [fixturePeriodKeys, fixtureFilters.matchdays, fixturesData]);

  return (
    <div className="components light flex flex-col max-w-[1281px] mx-auto w-full min-h-screen bg-[#f5f5f5]">
      {/* Header */}
      <div className="flex flex-col gap-0 bg-white px-4 pt-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-4">
          <Typography
            as="h1"
            variant="headline050"
            className="text-[--page-header-header] !font-[900] shrink-0 uppercase md:!text-40 md:!leading-44"
          >
            Stats Centre
          </Typography>
          <TabGroup
            tabs={[
              { id: 'player-stats', label: 'Player Stats' },
              { id: 'fixtures', label: 'Fixtures' },
              { id: 'top-transfers', label: 'Most Transferred' },
            ]}
            selectedId={activeTab}
            onChangeTab={id => {
              const nextTab = id as Tab;
              setActiveTab(nextTab);
              if (nextTab !== activeTab) {
                trackFantasyEvent('statsCentreDataToggle', {
                  toggle_name:
                    nextTab === 'player-stats'
                      ? 'Player Stats'
                      : nextTab === 'fixtures'
                        ? 'Fixtures'
                        : 'Most Transferred',
                });
              }
            }}
          />
        </div>

        {activeTab === 'player-stats' ? (
          <PlayerStatsFilterBar
            filters={playerFilters}
            onChange={setPlayerFilters}
            teamOptions={teamOptions}
            searchText={searchText}
            onSearchChange={setSearchText}
          />
        ) : activeTab === 'fixtures' ? (
          <FixturesFilterBar
            filters={fixtureFilters}
            onChange={setFixtureFilters}
            teamOptions={fixtureTeamOptions}
            periodOptions={fixturePeriodOptions}
            periodEmptyValue={periodLabels.long}
          />
        ) : null}
      </div>

      {/* Active filter chips */}
      {activeTab !== 'top-transfers' && (
        <ActiveFilterChips
          filters={activeFilters}
          onRemove={handleRemoveFilter}
          onClear={handleClearFilters}
        />
      )}

      {/* Scrollable table — player tab only */}
      {activeTab === 'player-stats' && (
        <div className="overflow-x-auto bg-white mt-0.5">
          <div className="min-w-max w-full">
            <StatsColumnHeaders
              sort={sort}
              onSort={handleSort}
              activeCategoryKeys={playerFilters.category.filter(k => k !== 'all')}
              activePpmKeys={playerFilters.ppmMetrics.filter(k => k !== 'all')}
            />
            {isLoading && (
              <div className="flex items-center justify-center h-64">
                <Typography variant="paragraph020" className="text-[--color-neutral-600]">
                  Loading players…
                </Typography>
              </div>
            )}
            {isError && (
              <div className="flex items-center justify-center h-64">
                <Typography variant="paragraph020" className="text-red-500">
                  Failed to load player stats. Please try again.
                </Typography>
              </div>
            )}
            {!isLoading && !isError && visiblePlayers.length === 0 && (
              <div className="flex items-center justify-center h-64">
                <Typography variant="paragraph020" className="text-[--color-neutral-600]">
                  No players match the selected filters.
                </Typography>
              </div>
            )}
            {!isLoading &&
              !isError &&
              visiblePlayers.map(player => (
                <PlayerRow
                  key={player.playerId}
                  player={player}
                  activeCategoryKeys={playerFilters.category.filter(k => k !== 'all')}
                  activePpmKeys={playerFilters.ppmMetrics.filter(k => k !== 'all')}
                />
              ))}

            {/* Load More */}
            {!isLoading && !isError && (hasNextPage || isFetchingNextPage) && (
              <div className="flex items-center justify-center py-6 border-t border-[#eae9fd]">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage || !hasNextPage}
                  className={twMerge(
                    'px-6 py-3 rounded h-12 max-w-[800px] text-sm font-bold transition-colors border bg-white',
                    isFetchingNextPage || !hasNextPage
                      ? 'text-[#888] border-[#ccc] cursor-not-allowed'
                      : 'text-[#0072EE] border-[#0072EE] hover:bg-[#f0f8ff]'
                  )}
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content — fixtures tab */}
      {/* Fixtures tab */}
      {activeTab === 'fixtures' && (
        <div className="flex-1 bg-[#f5f5f5] mt-0.5">
          <div className="px-[--padding-md] md:px-[--padding-7xl] py-6">
            {fixturesLoading && (
              <div className="flex items-center justify-center h-40">
                <Typography variant="paragraph020" className="text-[--color-neutral-600]">
                  Loading fixtures…
                </Typography>
              </div>
            )}
            {fixturesError && (
              <div className="flex items-center justify-center h-40">
                <Typography variant="paragraph020" className="text-red-500">
                  Failed to load fixtures. Please try again.
                </Typography>
              </div>
            )}
            {!fixturesLoading && !fixturesError && fixturesData && (
              <FixtureGrid
                teams={fixturesData.teams}
                periodKeys={visibleFixturePeriodKeys}
                selectedTeams={fixtureFilters.teams}
              />
            )}
          </div>
        </div>
      )}
      {activeTab === 'top-transfers' && (
        <div className="flex-1 bg-[#f5f5f5] px-[--padding-md] py-[--padding-xl] md:px-[--padding-7xl]">
          <TopTransfersPanel tournamentCalendarId={tournamentCalendarId} />
        </div>
      )}
      <ContentAdSlot id="ad-boa-leaderboard" className="bg-white" />
    </div>
  );
}
